import { useEffect, useRef, useState } from "react";
import { router } from "@inertiajs/react";
import AppShell from "../../components/Shell/AppShell";
import CatalogDocument from "../../components/catalogs/CatalogDocument";
import { modeLabel } from "../../lib/generationModes";
import { common, pick } from "../../lib/i18n";
import { getCsrfToken } from "../../lib/csrf";
import "../../styles/catalog.css";

const copy = {
  pl: { subtitle: "Złóż katalog z gotowych grafik, a następnie pobierz go jako PDF.", printPdf: "Drukuj / Pobierz PDF", name: "Nazwa", brandCover: "Marka (okładka)", none: "- brak -", layout: "Układ", onePerPage: "1 produkt / strona", gridPerPage: "2 produkty / strona", addProduct: "Dodaj produkt", allAdded: "- wszystkie dodane -", choose: "- wybierz -", saving: "Zapisuję…", saved: "Zapisano", saveError: "Błąd zapisu", visuals: "grafik", moveUp: "W górę", moveDown: "W dół", autoHero: "Automatyczny wybór grafiki głównej", emptyCatalog: "Pusty katalog", emptyHint: "Dodaj produkty, żeby zobaczyć podgląd stron katalogu." },
  en: { subtitle: "Assemble the catalog from ready visuals, then download it as a PDF.", printPdf: "Print / Download PDF", name: "Name", brandCover: "Brand (cover)", none: "- none -", layout: "Layout", onePerPage: "1 product / page", gridPerPage: "2 products / page", addProduct: "Add product", allAdded: "- all added -", choose: "- choose -", saving: "Saving…", saved: "Saved", saveError: "Save error", visuals: "visuals", moveUp: "Up", moveDown: "Down", autoHero: "Automatic hero image selection", emptyCatalog: "Empty catalog", emptyHint: "Add products to preview the catalog pages." },
};
import type { CatalogBrandOption, CatalogDetail, CatalogItem, CurrentUser, FeatureFlags, ProductSummary } from "../../types";

type Props = {
  catalog: CatalogDetail;
  available_products: { id: number; name: string }[];
  brands: CatalogBrandOption[];
  current_user: CurrentUser;
  products?: ProductSummary[];
  feature_flags?: FeatureFlags;
};

const csrf = () => getCsrfToken();

export default function CatalogShow({ catalog, available_products, brands, current_user, products = [], feature_flags }: Props) {
  const initials = current_user.name.split(" ").map((word) => word[0]).join("").slice(0, 2).toUpperCase();
  const canEdit = current_user.role === "admin" || current_user.role === "moderator";
  const areBrandsEnabled = feature_flags?.brands_enabled ?? true;
  const t = pick(copy);
  const c = common();

  const [name, setName] = useState(catalog.name);
  const [brandId, setBrandId] = useState<number | null>(areBrandsEnabled ? catalog.brand_id : null);
  const [layout, setLayout] = useState(catalog.layout);
  const [items, setItems] = useState<CatalogItem[]>(catalog.items);
  const [saveState, setSaveState] = useState<"saved" | "saving" | "error">("saved");
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const saveSeqRef = useRef(0);
  const nameTimerRef = useRef<number | null>(null);

  useEffect(() => {
    setName(catalog.name);
    setBrandId(areBrandsEnabled ? catalog.brand_id : null);
    setLayout(catalog.layout);
    setItems(catalog.items);
  }, [catalog, areBrandsEnabled]);

  useEffect(
    () => () => {
      if (nameTimerRef.current !== null) window.clearTimeout(nameTimerRef.current);
    },
    [],
  );

  type HeroPair = { product_id: number; hero_output_id: number | null };
  type Overrides = Partial<{ name: string; brandId: number | null; layout: string; pairs: HeroPair[] }>;

  const itemPairs = (list: CatalogItem[]): HeroPair[] =>
    list.map((item) => ({ product_id: item.product_id, hero_output_id: item.hero_output_id ?? null }));

  const sendPayload = (payload: object) => {
    const seq = ++saveSeqRef.current;
    setSaveState("saving");
    const run = async () => {
      try {
        const response = await fetch(`/catalogs/${catalog.id}`, {
          method:  "PATCH",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf() },
          body:    JSON.stringify(payload),
        });
        if (seq === saveSeqRef.current) setSaveState(response.ok ? "saved" : "error");
      } catch {
        if (seq === saveSeqRef.current) setSaveState("error");
      }
    };
    saveQueueRef.current = saveQueueRef.current.then(run);
    return saveQueueRef.current;
  };

  const retry = () => void persist();

  const persist = (overrides: Overrides = {}) => {
    const payload = {
      name:     "name" in overrides ? overrides.name : name,
      brand_id: areBrandsEnabled ? ("brandId" in overrides ? overrides.brandId : brandId) : null,
      layout:   "layout" in overrides ? overrides.layout : layout,
      items:    overrides.pairs ?? itemPairs(items),
    };
    return sendPayload(payload);
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (nameTimerRef.current !== null) window.clearTimeout(nameTimerRef.current);
    nameTimerRef.current = window.setTimeout(() => void persist({ name: value }), 800);
  };

  const commitName = () => {
    if (nameTimerRef.current !== null) window.clearTimeout(nameTimerRef.current);
    void persist({ name });
  };

  const addProduct = async (productId: number) => {
    if (!productId) return;
    await persist({ pairs: [...itemPairs(items), { product_id: productId, hero_output_id: null }] });
    router.reload({ only: ["catalog"] });
  };

  const removeProduct = (productId: number) => {
    const next = items.filter((item) => item.product_id !== productId);
    setItems(next);
    void persist({ pairs: itemPairs(next) });
  };

  const setHero = (productId: number, outputId: number | null) => {
    const next = items.map((item) => (item.product_id === productId ? { ...item, hero_output_id: outputId } : item));
    setItems(next);
    void persist({ pairs: itemPairs(next) });
  };

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    const current = items[index];
    const swapped = items[target];
    if (!current || !swapped) return;
    const next = [...items];
    next[index] = swapped;
    next[target] = current;
    setItems(next);
    void persist({ pairs: itemPairs(next) });
  };

  const availableToAdd = available_products.filter((product) => !items.some((item) => item.product_id === product.id));
  const brand = areBrandsEnabled ? brands.find((option) => option.id === brandId) ?? null : null;

  return (
    <AppShell products={products} user={{ name: current_user.name, initials, role: current_user.role }}>
      <div className="no-print">
        <div className="page" style={{ maxWidth: "none" }}>
          <div className="page-hd">
            <div>
              <h2>{name}</h2>
              <p>{t.subtitle}</p>
            </div>
            <div className="right">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={items.length === 0}
                onClick={() => window.open(`/catalogs/${catalog.id}/print`, "_blank", "noopener")}
              >
                {t.printPdf}
              </button>
            </div>
          </div>

          {canEdit && (
            <div className="catalog-controls">
              <div className="field" style={{ flex: 2 }}>
                <label>{t.name}</label>
                <input
                  className="input"
                  value={name}
                  maxLength={120}
                  onChange={(event) => handleNameChange(event.target.value)}
                  onBlur={commitName}
                />
              </div>
              {areBrandsEnabled && (
                <div className="field">
                  <label>{t.brandCover}</label>
                  <select
                    className="input"
                    value={brandId ?? ""}
                    onChange={(event) => {
                      const value = event.target.value ? Number(event.target.value) : null;
                      setBrandId(value);
                      persist({ brandId: value });
                    }}
                  >
                    <option value="">{t.none}</option>
                    {brands.map((option) => (
                      <option key={option.id} value={option.id}>{option.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="field">
                <label>{t.layout}</label>
                <select
                  className="input"
                  value={layout}
                  onChange={(event) => {
                    setLayout(event.target.value);
                    persist({ layout: event.target.value });
                  }}
                >
                  <option value="one_per_page">{t.onePerPage}</option>
                  <option value="grid">{t.gridPerPage}</option>
                </select>
              </div>
              <div className="field">
                <label>{t.addProduct}</label>
                <select
                  className="input"
                  value=""
                  disabled={availableToAdd.length === 0}
                  onChange={(event) => addProduct(Number(event.target.value))}
                >
                  <option value="">{availableToAdd.length === 0 ? t.allAdded : t.choose}</option>
                  {availableToAdd.map((product) => (
                    <option key={product.id} value={product.id}>{product.name}</option>
                  ))}
                </select>
              </div>
              <span style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 8, paddingBottom: 8 }}>
                {saveState === "saving" && <span style={{ color: "var(--muted)" }}>{t.saving}</span>}
                {saveState === "saved" && <span style={{ color: "var(--muted)" }}>{t.saved}</span>}
                {saveState === "error" && (
                  <>
                    <span style={{ color: "var(--danger)" }}>{t.saveError}</span>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={retry}>{c.actions.retry}</button>
                  </>
                )}
              </span>
            </div>
          )}

          {canEdit && items.length > 0 && (
            <div className="panel" style={{ marginBottom: 18 }}>
              <div className="panel-body" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {items.map((item, index) => (
                  <div
                    key={item.product_id}
                    style={{
                      display:       "flex",
                      flexDirection: "column",
                      gap:           8,
                      paddingBottom: index === items.length - 1 ? 0 : 10,
                      borderBottom:  index === items.length - 1 ? "none" : "1px solid var(--border)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                      <span style={{ color: "var(--muted)", width: 22, fontFamily: "'JetBrains Mono', monospace" }}>{index + 1}.</span>
                      <span style={{ flex: 1 }}>{item.name}</span>
                      <span style={{ fontSize: 11, color: item.outputs.length === 0 ? "var(--danger)" : "var(--muted)" }}>
                        {item.outputs.length} {t.visuals}
                      </span>
                      <button type="button" className="btn btn-icon btn-sm" aria-label={t.moveUp} disabled={index === 0} onClick={() => move(index, -1)}>↑</button>
                      <button type="button" className="btn btn-icon btn-sm" aria-label={t.moveDown} disabled={index === items.length - 1} onClick={() => move(index, 1)}>↓</button>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeProduct(item.product_id)}>{c.actions.delete}</button>
                    </div>
                    {item.outputs.length > 0 && (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", paddingLeft: 30 }}>
                        <button
                          type="button"
                          aria-pressed={item.hero_output_id === null}
                          title={t.autoHero}
                          onClick={() => setHero(item.product_id, null)}
                          style={{
                            width:      40,
                            height:     40,
                            borderRadius: 6,
                            fontSize:   10,
                            cursor:     "pointer",
                            background: "transparent",
                            color:      "var(--muted)",
                            border:     `2px solid ${item.hero_output_id === null ? "var(--accent)" : "var(--border)"}`,
                          }}
                        >
                          Auto
                        </button>
                        {item.outputs.map((output) => (
                          <button
                            type="button"
                            key={output.id}
                            aria-pressed={item.hero_output_id === output.id}
                            title={modeLabel(output.mode)}
                            onClick={() => setHero(item.product_id, output.id)}
                            style={{
                              width:      40,
                              height:     40,
                              padding:    0,
                              borderRadius: 6,
                              overflow:   "hidden",
                              cursor:     "pointer",
                              background: "transparent",
                              border:     `2px solid ${item.hero_output_id === output.id ? "var(--accent)" : "var(--border)"}`,
                            }}
                          >
                            <img src={output.image_url} alt={modeLabel(output.mode)} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {items.length === 0 && (
            <div className="empty">
              <h4>{t.emptyCatalog}</h4>
              <p>{t.emptyHint}</p>
            </div>
          )}
        </div>
      </div>

      <CatalogDocument name={name} brand={brand} layout={layout} items={items} />
    </AppShell>
  );
}
